import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ImageOff, ExternalLink } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { productImage, productTitle, productDesc, productShortDesc, formatPrice } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ProductCard = ({ product, index = 0 }) => {
  const { t, lang } = useI18n();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const img = productImage(product, 600);
  const isAmazon = product.product_type === "amazon";
  const amazonKind = product.amazon_kind || "book";
  const isBook = amazonKind !== "product";
  const kindLabel = isBook ? (lang === "en" ? "Book" : "Libro") : (lang === "en" ? "Product" : "Prodotto");
  const amazonBtnLabel = lang === "en" ? "View on Amazon" : "Vedi su Amazon";
  const author = lang === "en" ? (product.subtitle_en || product.subtitle_it) : (product.subtitle_it || product.subtitle_en);

  const add = (e) => {
    e.stopPropagation();
    addItem(product, 1);
    toast.success(t("common.addToCart"), { description: productTitle(product, lang) });
  };

  const goAmazon = (e) => {
    e.stopPropagation();
    if (product.amazon_url) window.open(product.amazon_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/prodotti/${product.id}`)}
      className="group cursor-pointer bg-white rounded-2xl border border-brand-navy/10 overflow-hidden lift flex flex-col"
    >
      <div className="relative aspect-[4/3] bg-brand-light overflow-hidden">
        {img ? (
          <img src={img} alt={productTitle(product, lang)} loading="lazy"
            className="w-full h-full object-contain p-3" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-brand-navy/30"><ImageOff className="h-10 w-10" /></div>
        )}
        {isAmazon && (
          <span data-testid="amazon-badge" className="absolute top-3 left-3 bg-[#FF9900] text-brand-navy text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            Amazon
          </span>
        )}
        {isAmazon && (
          <span data-testid={`kind-badge-${product.id}`} className="absolute top-3 right-3 bg-white/95 text-brand-navy text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-brand-navy/10 shadow-sm">
            {kindLabel}
          </span>
        )}
        {product.is_demo && !isAmazon && (
          <span data-testid="demo-badge" className="absolute top-3 left-3 bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            {t("common.demo")}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-semibold text-base text-brand-navy leading-snug line-clamp-2">{productTitle(product, lang)}</h3>
        {isAmazon && isBook && author && <p className="mt-1 text-xs text-brand-navy/50 italic line-clamp-1">{author}</p>}
        <p className="mt-2 text-sm text-brand-navy/60 line-clamp-2 flex-1">{productShortDesc(product, lang) || productDesc(product, lang)}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="font-heading font-bold text-lg text-brand-navy">{product.price > 0 ? formatPrice(product.price) : ""}</span>
          {isAmazon ? (
            <Button data-testid={`amazon-btn-${product.id}`} onClick={goAmazon} size="sm"
              className="rounded-full bg-[#FF9900] hover:bg-[#e88b00] text-brand-navy font-semibold gap-1.5">
              <ExternalLink className="h-4 w-4" /> <span>{amazonBtnLabel}</span>
            </Button>
          ) : (
            <Button data-testid={`add-cart-${product.id}`} onClick={add} size="sm"
              className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-1.5">
              <ShoppingCart className="h-4 w-4" /> <span className="hidden sm:inline">{t("common.addToCart")}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
